import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  TrendingUp,
  Award,
  Target,
  MessageSquare,
  Volume2,
  BarChart3,
  Home,
  RefreshCw,
  Download,
  Star,
  Zap,
  ArrowUp,
  ArrowDown,
  Sparkles
} from 'lucide-react';
import { LoadingOverlay } from '@/components/ui/loading';
import { cn } from '@/lib/utils';

interface FeedbackAnalysisProps {
  feedbackData: any;
  isLoading: boolean;
  onClose: () => void;
}

export function FeedbackAnalysis({ feedbackData, isLoading, onClose }: FeedbackAnalysisProps) {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingOverlay state="feedback" />
      </div>
    );
  }

  const scores = feedbackData?.scores || {};
  const feedback = feedbackData?.feedback || {};
  const insights = feedbackData?.insights || [];

  const overallScore = scores.overall || 0;
  const performanceLevel = overallScore >= 80 ? 'excellent' : overallScore >= 60 ? 'good' : 'needs-improvement';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                performanceLevel === 'excellent' ? "bg-green-100" :
                performanceLevel === 'good' ? "bg-blue-100" : "bg-orange-100"
              )}>
                <Award className={cn(
                  "h-6 w-6",
                  performanceLevel === 'excellent' ? "text-green-600" :
                  performanceLevel === 'good' ? "text-blue-600" : "text-orange-600"
                )} />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Practice Session Complete</h1>
                <p className="text-sm text-gray-500 mt-0.5">Here's your detailed performance analysis</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </Button>
              <Button
                onClick={() => window.location.reload()}
                size="sm"
                className="flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Practice Again</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Overall Performance</h2>

              <div className="relative w-48 h-48 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 88}`}
                    strokeDashoffset={`${2 * Math.PI * 88 * (1 - overallScore / 100)}`}
                    className={cn(
                      "transition-all duration-1000 ease-out",
                      performanceLevel === 'excellent' ? "text-green-500" :
                      performanceLevel === 'good' ? "text-blue-500" : "text-orange-500"
                    )}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-gray-900">{overallScore}%</span>
                  <span className="text-sm text-gray-500 capitalize">{performanceLevel}</span>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { name: 'Communication', score: scores.communication || 0, icon: MessageSquare, color: 'blue' },
                  { name: 'Product Knowledge', score: scores.productKnowledge || 0, icon: Target, color: 'green' },
                  { name: 'Sales Technique', score: scores.salesTechnique || 0, icon: TrendingUp, color: 'purple' },
                  { name: 'Engagement', score: scores.engagement || 0, icon: Volume2, color: 'orange' },
                ].map((metric) => (
                  <div key={metric.name}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <metric.icon className={cn(
                          "h-4 w-4",
                          metric.color === 'blue' ? "text-blue-500" :
                          metric.color === 'green' ? "text-green-500" :
                          metric.color === 'purple' ? "text-purple-500" :
                          "text-orange-500"
                        )} />
                        <span className="text-sm font-medium text-gray-700">{metric.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{metric.score}%</span>
                    </div>
                    <Progress
                      value={metric.score}
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6"
            >
              <h3 className="text-sm font-medium text-gray-900 mb-3">Session Rating</h3>
              <div className="flex justify-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={cn(
                      "h-8 w-8 transition-colors",
                      star <= Math.round(overallScore / 20)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    )}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          <div className="col-span-1 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Key Strengths</h3>
              </div>

              <div className="space-y-3">
                {(feedback.strengths || [
                  'Excellent rapport building with the customer',
                  'Strong product knowledge demonstration',
                  'Effective handling of initial objections'
                ]).map((strength: string, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <ArrowUp className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed">{strength}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Growth Opportunities</h3>
              </div>

              <div className="space-y-3">
                {(feedback.improvements || [
                  'Ask more open-ended discovery questions',
                  'Provide more specific value propositions',
                  'Create stronger urgency for decision making'
                ]).map((improvement: string, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <Zap className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed">{improvement}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Key Moments</h3>
              </div>

              <div className="space-y-3">
                {(insights.length > 0 ? insights : [
                  {
                    topic: 'Opening',
                    type: 'positive',
                    message: 'Great job establishing rapport in the first 30 seconds'
                  },
                  {
                    topic: 'Discovery',
                    type: 'tip',
                    message: 'Consider asking about their current solution earlier'
                  },
                  {
                    topic: 'Objection Handling',
                    type: 'positive',
                    message: 'Excellent response to the pricing concern'
                  },
                  {
                    topic: 'Closing',
                    type: 'tip',
                    message: 'Try to secure a specific follow-up date and time'
                  }
                ]).map((insight: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className={cn(
                      "p-4 rounded-xl",
                      insight.type === 'positive'
                        ? "bg-green-50 border border-green-200"
                        : "bg-blue-50 border border-blue-200"
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{insight.topic}</h4>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full font-medium",
                        insight.type === 'positive'
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      )}>
                        {insight.type === 'positive' ? 'Well Done' : 'Pro Tip'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{insight.message}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl">
                <div className="flex items-center space-x-2 mb-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <h4 className="text-sm font-medium text-gray-900">Recommended Next Steps</h4>
                </div>
                <ul className="space-y-1 text-xs text-gray-700">
                  <li>• Practice discovery questions with a colleague</li>
                  <li>• Review competitor comparison materials</li>
                  <li>• Work on creating urgency techniques</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Ready for another round?</h3>
              <p className="text-sm text-gray-500 mt-0.5">Practice makes perfect. Each session helps you improve.</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="px-6"
              >
                <Home className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <Button
                onClick={() => window.location.reload()}
                className="px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Start New Session
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}